import { Modal } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useMemo, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../Buttons/Button";
import IpoStatusChip from "../dashboard/ipos/IpoStatusChip";
import { allotIpo } from "../../store/actions/ipos/allotIpo";
import { clearAllotmentResult } from "../../store/reducers/iposSlice";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";

const formatMoney = (currency, value) =>
  value != null
    ? `${getCurrencySymbol(currency)} ${Number(value).toLocaleString()}`
    : "-";

const formatNumber = (value) =>
  value != null ? Number(value).toLocaleString() : "-";

// Only subscriptions still waiting on the registrar can be allotted.
const AWAITING_STATUSES = ["submitted", "processing"];

// App.css styles every <table> in the app as a fixed six-column CSS grid
// (table{display:grid} + thead/tbody/tr{display:contents}), so a table with a
// different column count has its cells flow into the wrong columns. Restore
// normal table layout for the tables inside this modal only.
const tableReset = {
  "& table": {
    display: "table",
    width: "100%",
    marginLeft: 0,
    borderCollapse: "collapse",
    tableLayout: "auto",
  },
  "& thead": { display: "table-header-group" },
  "& tbody": { display: "table-row-group" },
  "& tr": { display: "table-row" },
  "& th, & td": {
    display: "table-cell",
    padding: "12px",
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
    verticalAlign: "middle",
  },
};

const subscriberName = (row) =>
  row?.user_name ||
  row?.user?.name ||
  row?.user_email ||
  row?.user?.email ||
  `User #${row?.user_id}`;

function AllotIpoModal({ open, onClose, ipo, subscriptions = [] }) {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.ipos?.allotPending);
  const result = useSelector((state) => state?.ipos?.allotmentResult);

  const [mode, setMode] = useState("full");
  // { [subscription_id]: "700" } — kept as strings so the input can be
  // cleared while typing.
  const [allotted, setAllotted] = useState({});

  const pending = useMemo(
    () =>
      (subscriptions || []).filter((row) =>
        AWAITING_STATUSES.includes(row?.status)
      ),
    [subscriptions]
  );

  // Seed the inputs once per open. `pending` is read through a ref so the
  // refetch that follows a successful allotment doesn't re-run this and wipe
  // the results view.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    if (open) {
      setMode("full");
      setAllotted(
        pendingRef.current.reduce((acc, row) => {
          acc[row.id] = String(row?.shares_requested ?? 0);
          return acc;
        }, {})
      );
      dispatch(clearAllotmentResult());
    }
  }, [open, dispatch]);

  const currency = ipo?.currency;
  const offerPrice = Number(ipo?.offer_price || 0);

  const clamp = (row, raw) => {
    if (raw === "") return "";
    const max = Number(row?.shares_requested ?? 0);
    const n = Math.floor(Number(raw));
    if (Number.isNaN(n) || n < 0) return "0";
    return String(Math.min(n, max));
  };

  const setAll = (fn) => {
    setAllotted(
      pending.reduce((acc, row) => {
        acc[row.id] = fn(row);
        return acc;
      }, {})
    );
  };

  const totals = useMemo(() => {
    const requested = pending.reduce(
      (sum, row) => sum + Number(row?.shares_requested || 0),
      0
    );
    const toAllot =
      mode === "full"
        ? requested
        : pending.reduce(
            (sum, row) => sum + Number(allotted[row.id] || 0),
            0
          );
    return {
      requested,
      toAllot,
      refund: (requested - toAllot) * offerPrice,
    };
  }, [pending, allotted, mode, offerPrice]);

  const hasBlankRow =
    mode === "manual" && pending.some((row) => allotted[row.id] === "");

  const overOffer =
    ipo?.total_shares_on_offer != null &&
    totals.toAllot > Number(ipo.total_shares_on_offer);

  const handleSubmit = () => {
    const data =
      mode === "full"
        ? { allot_all_in_full: true }
        : {
            allotments: pending.map((row) => ({
              subscription_id: row.id,
              shares_allotted: Number(allotted[row.id] || 0),
            })),
          };
    dispatch(allotIpo({ id: ipo?.id, data }));
  };

  const handleClose = () => {
    dispatch(clearAllotmentResult());
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 1200,
          maxWidth: "96vw",
          maxHeight: "94vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 2,
          ...tableReset,
        }}
      >
        <IconButton
          aria-label="close"
          sx={{ position: "absolute", top: 19, right: 10 }}
          onClick={handleClose}
        >
          <ClearIcon />
        </IconButton>

        <div className="w-full p-3">
          {result ? (
            <>
              <h1 className="font-semibold text-2xl mb-1">Allotment Applied</h1>
              <p className="text-sm mb-4 text-[#475467]">
                {result?.applied ?? 0} applied · {result?.failed ?? 0} failed.
                Each subscription settles on its own, so failed rows can be
                re-submitted after the issue is fixed.
              </p>

              {result?.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  {[
                    ["Shares Requested", formatNumber(result.summary.shares_requested)],
                    ["Shares Allotted", formatNumber(result.summary.shares_allotted)],
                    ["Allotted Value", formatMoney(currency, result.summary.allotted_amount)],
                    ["Refunded", formatMoney(currency, result.summary.refunded_amount)],
                    ["Admin Fee Retained", formatMoney(currency, result.summary.admin_fee)],
                    ["Total Debited", formatMoney(currency, result.summary.total_debited)],
                    ["Subscribers", formatNumber(result.summary.subscribers_count)],
                    ["Still Awaiting", formatNumber(result.summary.awaiting_allotment)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="border border-[#EAECF0] rounded-lg p-3"
                    >
                      <p className="text-xs text-[#475467] mb-1">{label}</p>
                      <p className="font-[600] text-sm">{value}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto border border-[#EAECF0] rounded-lg mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] text-[#475467] text-xs">
                    <tr>
                      <th className="text-left p-3">Reference</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Requested</th>
                      <th className="text-right p-3">Allotted</th>
                      <th className="text-right p-3">Refund</th>
                      <th className="text-left p-3">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result?.results || []).map((row) => (
                      <tr
                        key={row.subscription_id}
                        className="border-t border-[#EAECF0]"
                      >
                        <td className="p-3 font-[500]">{row?.reference}</td>
                        <td className="p-3">
                          <IpoStatusChip
                            status={row?.status}
                            kind="subscription"
                          />
                        </td>
                        <td className="p-3 text-right">
                          {formatNumber(row?.shares_requested)}
                        </td>
                        <td className="p-3 text-right">
                          {formatNumber(row?.shares_allotted)}
                        </td>
                        <td className="p-3 text-right">
                          {formatMoney(currency, row?.refund_amount)}
                        </td>
                        <td className="p-3 text-xs text-[#B42318]">
                          {row?.message || row?.error || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Done"
                  width="140px"
                  height="44px"
                  color="#fff"
                  onClick={handleClose}
                />
              </div>
            </>
          ) : (
            <>
              <h1 className="font-semibold text-2xl mb-1">Apply Allotment</h1>
              <p className="text-sm mb-4 text-[#475467]">
                Enter the registrar&apos;s results for{" "}
                <span className="font-[600]">{ipo?.stock?.symbol}</span>. Each
                subscriber is allotted whole shares at{" "}
                {formatMoney(currency, offerPrice)}; the difference between what
                they paid for and what they receive is refunded automatically.
                The admin fee is retained in full, even on a nil allotment.
              </p>

              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                {[
                  {
                    id: "full",
                    label: "Allot everyone in full",
                    hint: "Every awaiting subscription gets exactly what it requested.",
                  },
                  {
                    id: "manual",
                    label: "Enter results per subscription",
                    hint: "Type the allotted shares for each row. 0 = not allotted.",
                  },
                ].map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex-1 flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${
                      mode === opt.id
                        ? "border-[#159AA8] bg-[#F0FAFB]"
                        : "border-[#EAECF0]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="allot-mode"
                      checked={mode === opt.id}
                      onChange={() => setMode(opt.id)}
                      className="mt-1 accent-[#159AA8]"
                    />
                    <span>
                      <span className="block text-sm font-[600]">{opt.label}</span>
                      <span className="block text-xs text-[#475467]">{opt.hint}</span>
                    </span>
                  </label>
                ))}
              </div>

              {pending.length < 1 ? (
                <div className="text-sm text-[#475467] border border-[#EAECF0] rounded-lg p-4 mb-6">
                  No subscriptions are awaiting allotment.
                </div>
              ) : (
                <>
                  {mode === "manual" && (
                    <div className="flex justify-end gap-2 mb-2">
                      <CustomButton
                        variant="outlined"
                        customColor="#fff"
                        children="Fill all in full"
                        width="max-content"
                        height="32px"
                        padding="0 12px"
                        borderColor="#D0D5DD"
                        color="#344054"
                        fz="12px"
                        onClick={() =>
                          setAll((row) => String(row?.shares_requested ?? 0))
                        }
                      />
                      <CustomButton
                        variant="outlined"
                        customColor="#fff"
                        children="Set all to 0"
                        width="max-content"
                        height="32px"
                        padding="0 12px"
                        borderColor="#D0D5DD"
                        color="#344054"
                        fz="12px"
                        onClick={() => setAll(() => "0")}
                      />
                    </div>
                  )}

                  <div className="overflow-x-auto border border-[#EAECF0] rounded-lg mb-4 max-h-[55vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F9FAFB] text-[#475467] text-xs sticky top-0">
                        <tr>
                          <th className="text-left p-3">Reference</th>
                          <th className="text-left p-3">Subscriber</th>
                          <th className="text-right p-3">Requested</th>
                          <th className="text-right p-3">Allot</th>
                          <th className="text-right p-3">Refund (est.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((row) => {
                          const value =
                            mode === "full"
                              ? Number(row?.shares_requested ?? 0)
                              : Number(allotted[row.id] || 0);
                          const refund =
                            (Number(row?.shares_requested ?? 0) - value) *
                            offerPrice;
                          return (
                            <tr
                              key={row.id}
                              className="border-t border-[#EAECF0]"
                            >
                              <td className="p-3 font-[500] whitespace-nowrap">
                                {row?.reference}
                              </td>
                              <td className="p-3">{subscriberName(row)}</td>
                              <td className="p-3 text-right">
                                {formatNumber(row?.shares_requested)}
                              </td>
                              <td className="p-3 text-right">
                                {mode === "full" ? (
                                  formatNumber(row?.shares_requested)
                                ) : (
                                  <input
                                    type="number"
                                    min={0}
                                    max={row?.shares_requested ?? 0}
                                    step={1}
                                    value={allotted[row.id] ?? ""}
                                    onChange={(e) =>
                                      setAllotted((prev) => ({
                                        ...prev,
                                        [row.id]: clamp(row, e.target.value),
                                      }))
                                    }
                                    className="w-28 border border-[#D0D5DD] rounded-lg p-2 text-right outline-none"
                                  />
                                )}
                              </td>
                              <td className="p-3 text-right text-[#475467]">
                                {formatMoney(currency, refund)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="border border-[#EAECF0] rounded-lg p-3">
                      <p className="text-xs text-[#475467] mb-1">Shares Requested</p>
                      <p className="font-[600] text-sm">
                        {formatNumber(totals.requested)}
                      </p>
                    </div>
                    <div className="border border-[#EAECF0] rounded-lg p-3">
                      <p className="text-xs text-[#475467] mb-1">Shares to Allot</p>
                      <p
                        className="font-[600] text-sm"
                        style={{ color: overOffer ? "#B42318" : undefined }}
                      >
                        {formatNumber(totals.toAllot)}
                        {ipo?.total_shares_on_offer != null && (
                          <span className="text-xs text-[#475467] font-normal">
                            {" "}
                            / {formatNumber(ipo.total_shares_on_offer)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="border border-[#EAECF0] rounded-lg p-3">
                      <p className="text-xs text-[#475467] mb-1">Refund (est.)</p>
                      <p className="font-[600] text-sm">
                        {formatMoney(currency, totals.refund)}
                      </p>
                    </div>
                  </div>

                  {overOffer && (
                    <div className="text-xs text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded-lg p-3 mb-4">
                      Shares to allot exceed the total shares on offer.
                    </div>
                  )}
                </>
              )}

              <div className="flex mt-2 w-full items-center">
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Cancel"
                  width="100%"
                  height="44px"
                  borderColor="#D0D5DD"
                  color="#344054"
                  margin="0px 25px 0px 0px"
                  onClick={handleClose}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Apply Allotment"
                  width="100%"
                  height="44px"
                  loading={loading}
                  padding="10px"
                  color="#fff"
                  disabled={loading || pending.length < 1 || hasBlankRow}
                  onClick={handleSubmit}
                />
              </div>
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default AllotIpoModal;
